import { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Typography, Card, App, Select, Tag, Switch, Tooltip, Dropdown, Alert, Checkbox } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, RollbackOutlined, OrderedListOutlined, GroupOutlined, HolderOutlined, MoreOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import forecastFieldsService, {
  FORECAST_TYPES,
  type ForecastField,
  type CreateForecastFieldRequest,
  type UpdateForecastFieldRequest,
  type ForecastType,
} from '../../api/forecastFieldsService';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

/** Format update_datetime - handles BigQuery format { value: string } or plain ISO string. Shows UTC. */
function formatUpdateDatetime(val: string | { value?: string } | null | undefined): string {
  const str = !val ? null : typeof val === 'string' ? val : (val as { value?: string })?.value;
  if (!str) return '-';
  const date = new Date(str);
  return isNaN(date.getTime()) ? '-' : `${date.toLocaleString(undefined, { timeZone: 'UTC', hour12: false })} UTC`;
}

// Forecast type options for the select dropdown. Store concrete values only.
const FORECAST_TYPE_OPTIONS: { value: ForecastType; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'minutely', label: 'Minutely' },
];

// Tag colors for forecast types
const FORECAST_TYPE_COLORS: Record<ForecastType, string> = {
  daily: 'blue',
  hourly: 'green',
  minutely: 'cyan',
};

interface ForecastFieldFormValues {
  api_name: string;
  db_name: string;
  display_name: string;
  unit?: string;
  forecast_types: ForecastType[];
  calculated: boolean;
  hidden: boolean;
  demo_only: boolean;
  // Granular visibility controls
  show_in_hourly_cards: boolean;
  show_in_hourly_table: boolean;
  show_in_daily_cards: boolean;
  show_in_daily_table: boolean;
  show_in_map_card: boolean;
  // Group for cards view
  card_group?: string;
  // Short display name for grouped display
  group_display_name?: string;
  // Position for ordering within groups
  group_position?: number;
  // Overall position for ordering fields
  position?: number;
}

// Interface for available groups
interface GroupOption {
  name: string; // Group name
  fieldCount: number; // Number of fields in this group
}

export const ForecastFieldsManagement = () => {
  const { message } = App.useApp();
  const { authState } = useAuth();
  const token = authState.token;
  const [fields, setFields] = useState<ForecastField[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<ForecastField | null>(null);
  const [form] = Form.useForm<ForecastFieldFormValues>();

  // Reorder modal state
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [reorderFields, setReorderFields] = useState<ForecastField[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  // Group reorder modal state (reorder fields within a group)
  const [isGroupReorderModalOpen, setIsGroupReorderModalOpen] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<GroupOption[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [groupFieldsToReorder, setGroupFieldsToReorder] = useState<ForecastField[]>([]);
  const [savingGroupOrder, setSavingGroupOrder] = useState(false);
  const [draggedGroupFieldIndex, setDraggedGroupFieldIndex] = useState<number | null>(null);
  const [dragOverGroupFieldIndex, setDragOverGroupFieldIndex] = useState<number | null>(null);
  const dragGroupFieldNodeRef = useRef<HTMLDivElement | null>(null);

  // Table container ref for dynamic scroll height
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  // Include soft-deleted fields in the list (for management UI)
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Fetch all forecast fields
  const fetchForecastFields = useCallback(async () => {
    setLoading(true);
    try {
      const data = await forecastFieldsService.getAllForecastFields(includeDeleted);
      setFields(data);
    } catch (error) {
      message.error('Failed to fetch forecast fields');
      console.error('Error fetching forecast fields:', error);
    } finally {
      setLoading(false);
    }
  }, [message, includeDeleted]);

  useEffect(() => {
    fetchForecastFields();
  }, [fetchForecastFields]);

  // Measure table container and set table body scroll height (header + footer)
  const TABLE_SCROLL_RESERVE_PX = 80;
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      setTableScrollY(Math.max(200, height - TABLE_SCROLL_RESERVE_PX));
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, fields.length]);

  // Handle create/edit modal
  const handleOpenModal = (field?: ForecastField) => {
    if (field) {
      setEditingField(field);
      // Convert null to undefined for form compatibility
      form.setFieldsValue({
        ...field,
        unit: field.unit ?? undefined,
        forecast_types: field.forecast_types,
        calculated: field.calculated ?? false,
        hidden: field.hidden ?? false,
        demo_only: field.demo_only ?? false,
        show_in_hourly_cards: field.show_in_hourly_cards ?? true,
        show_in_hourly_table: field.show_in_hourly_table ?? true,
        show_in_daily_cards: field.show_in_daily_cards ?? true,
        show_in_daily_table: field.show_in_daily_table ?? true,
        show_in_map_card: field.show_in_map_card ?? true,
        card_group: field.card_group ?? undefined,
        group_display_name: field.group_display_name ?? undefined,
        group_position: field.group_position ?? undefined,
        position: field.position ?? undefined,
      });
    } else {
      setEditingField(null);
      form.resetFields();
      // Set default values for new field
      form.setFieldsValue({
        forecast_types: ['daily', 'hourly'],
        calculated: false,
        hidden: false,
        demo_only: false,
        show_in_hourly_cards: true,
        show_in_hourly_table: true,
        show_in_daily_cards: true,
        show_in_daily_table: true,
        show_in_map_card: true,
        card_group: undefined,
        group_display_name: undefined,
        group_position: undefined,
        position: undefined,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingField(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!token) {
        message.error('Authentication required');
        return;
      }

      setSaving(true);

      if (editingField) {
        // Update existing forecast field
        const updates: UpdateForecastFieldRequest = {
          db_name: values.db_name,
          display_name: values.display_name,
          unit: values.unit || null,
          forecast_types: values.forecast_types,
          calculated: values.calculated,
          hidden: values.hidden,
          demo_only: values.demo_only,
          show_in_hourly_cards: values.show_in_hourly_cards,
          show_in_hourly_table: values.show_in_hourly_table,
          show_in_daily_cards: values.show_in_daily_cards,
          show_in_daily_table: values.show_in_daily_table,
          show_in_map_card: values.show_in_map_card,
          card_group: values.card_group || null,
          group_display_name: values.group_display_name || null,
          group_position: values.group_position ?? null,
          position: values.position ?? null,
        };

        await forecastFieldsService.updateForecastField(editingField.api_name, updates, token);
        message.success('Forecast field updated successfully');
      } else {
        // Create new forecast field
        const fieldData: CreateForecastFieldRequest = {
          api_name: values.api_name,
          db_name: values.db_name,
          display_name: values.display_name,
          unit: values.unit || null,
          forecast_types: values.forecast_types,
          calculated: values.calculated ?? false,
          hidden: values.hidden ?? false,
          demo_only: values.demo_only ?? false,
          show_in_hourly_cards: values.show_in_hourly_cards ?? true,
          show_in_hourly_table: values.show_in_hourly_table ?? true,
          show_in_daily_cards: values.show_in_daily_cards ?? true,
          show_in_daily_table: values.show_in_daily_table ?? true,
          show_in_map_card: values.show_in_map_card ?? true,
          card_group: values.card_group || null,
          group_display_name: values.group_display_name || null,
          group_position: values.group_position ?? null,
          position: values.position ?? null,
        };

        await forecastFieldsService.createForecastField(fieldData, token);
        message.success('Forecast field created successfully');
      }

      handleCloseModal();
      fetchForecastFields();
    } catch (error) {
      console.error('Error saving forecast field:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save forecast field';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (apiName: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await forecastFieldsService.deleteForecastField(apiName, token);
      message.success('Forecast field soft-deleted successfully');
      fetchForecastFields();
    } catch (error) {
      message.error('Failed to delete forecast field');
      console.error('Error deleting forecast field:', error);
    }
  };

  const handleRestore = async (apiName: string) => {
    try {
      if (!token) {
        message.error('Authentication required');
        return;
      }

      await forecastFieldsService.setForecastFieldDeleted(apiName, false, token);
      message.success('Forecast field restored successfully');
      fetchForecastFields();
    } catch (error) {
      message.error('Failed to restore forecast field');
      console.error('Error restoring forecast field:', error);
    }
  };

  // Reorder modal handlers
  const handleOpenReorderModal = () => {
    // Sort non-deleted fields by current position for the reorder modal
    const activeFields = fields.filter(f => !f.deleted);
    const sorted = [...activeFields].sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
    setReorderFields(sorted);
    setIsReorderModalOpen(true);
  };

  const handleCloseReorderModal = () => {
    setIsReorderModalOpen(false);
    setReorderFields([]);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    dragNodeRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add a slight delay to show the dragging state
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newFields = [...reorderFields];
    const [draggedItem] = newFields.splice(draggedIndex, 1);
    newFields.splice(dropIndex, 0, draggedItem);
    setReorderFields(newFields);
    setDragOverIndex(null);
  };

  const handleSaveOrder = async () => {
    if (!token) {
      message.error('Authentication required');
      return;
    }

    setSavingOrder(true);
    try {
      // Build array of fields with changed positions
      const changedFields = reorderFields
        .map((field, index) => ({
          api_name: field.api_name,
          position: index + 1,
        }))
        .filter((item, index) => reorderFields[index].position !== item.position);

      if (changedFields.length === 0) {
        message.info('No changes to save');
        handleCloseReorderModal();
        return;
      }

      // Batch update all positions in a single API call
      await forecastFieldsService.reorderForecastFields(changedFields, token);
      message.success('Field order updated successfully');
      handleCloseReorderModal();
      fetchForecastFields();
    } catch (error) {
      console.error('Error saving field order:', error);
      message.error('Failed to save field order');
    } finally {
      setSavingOrder(false);
    }
  };

  // Group reorder modal handlers (reorder fields within a group)
  const handleOpenGroupReorderModal = () => {
    // Build list of available groups (only from non-deleted fields)
    const groupMap = new Map<string, number>();
    
    fields.forEach(field => {
      if (field.card_group && !field.deleted) {
        const count = groupMap.get(field.card_group) || 0;
        groupMap.set(field.card_group, count + 1);
      }
    });

    const groups: GroupOption[] = [];
    groupMap.forEach((count, name) => {
      groups.push({ name, fieldCount: count });
    });
    
    // Sort groups alphabetically
    groups.sort((a, b) => a.name.localeCompare(b.name));
    
    setAvailableGroups(groups);
    setSelectedGroup(null);
    setGroupFieldsToReorder([]);
    setIsGroupReorderModalOpen(true);
  };

  const handleSelectGroup = (groupName: string) => {
    setSelectedGroup(groupName);
    // Get non-deleted fields for this group, sorted by group_position
    const groupFields = fields
      .filter(f => f.card_group === groupName && !f.deleted)
      .sort((a, b) => (a.group_position ?? 9999) - (b.group_position ?? 9999));
    setGroupFieldsToReorder(groupFields);
  };

  const handleCloseGroupReorderModal = () => {
    setIsGroupReorderModalOpen(false);
    setAvailableGroups([]);
    setSelectedGroup(null);
    setGroupFieldsToReorder([]);
    setDraggedGroupFieldIndex(null);
    setDragOverGroupFieldIndex(null);
  };

  const handleGroupFieldDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedGroupFieldIndex(index);
    dragGroupFieldNodeRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setTimeout(() => {
      if (dragGroupFieldNodeRef.current) {
        dragGroupFieldNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleGroupFieldDragEnd = () => {
    if (dragGroupFieldNodeRef.current) {
      dragGroupFieldNodeRef.current.style.opacity = '1';
    }
    setDraggedGroupFieldIndex(null);
    setDragOverGroupFieldIndex(null);
    dragGroupFieldNodeRef.current = null;
  };

  const handleGroupFieldDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedGroupFieldIndex !== null && draggedGroupFieldIndex !== index) {
      setDragOverGroupFieldIndex(index);
    }
  };

  const handleGroupFieldDragLeave = () => {
    setDragOverGroupFieldIndex(null);
  };

  const handleGroupFieldDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedGroupFieldIndex === null || draggedGroupFieldIndex === dropIndex) {
      setDragOverGroupFieldIndex(null);
      return;
    }

    const newFields = [...groupFieldsToReorder];
    const [draggedItem] = newFields.splice(draggedGroupFieldIndex, 1);
    newFields.splice(dropIndex, 0, draggedItem);
    setGroupFieldsToReorder(newFields);
    setDragOverGroupFieldIndex(null);
  };

  const handleSaveGroupFieldOrder = async () => {
    if (!token) {
      message.error('Authentication required');
      return;
    }

    setSavingGroupOrder(true);
    try {
      // Build array of fields with their new group_position
      const fieldUpdates = groupFieldsToReorder.map((field, index) => ({
        api_name: field.api_name,
        group_position: index + 1,
      }));

      // Filter to only include changed positions
      const changedFields = fieldUpdates.filter((update, index) => {
        const originalField = groupFieldsToReorder[index];
        return originalField.group_position !== update.group_position;
      });

      if (changedFields.length === 0) {
        message.info('No changes to save');
        handleCloseGroupReorderModal();
        return;
      }

      // Batch update group_position for all changed fields
      await forecastFieldsService.reorderForecastFieldsInGroup(changedFields, token);
      message.success(`Updated order for ${changedFields.length} field(s) in "${selectedGroup}"`);
      handleCloseGroupReorderModal();
      fetchForecastFields();
    } catch (error) {
      console.error('Error saving group field order:', error);
      message.error('Failed to save field order in group');
    } finally {
      setSavingGroupOrder(false);
    }
  };

  // Check if there are any groups to reorder (among non-deleted fields)
  const hasGroups = fields.some(f => f.card_group && !f.deleted);

  const columns: ColumnsType<ForecastField> = [
    {
      title: '',
      dataIndex: 'position',
      key: 'position',
      width: 60,
      sorter: (a, b) => (a.position ?? 9999) - (b.position ?? 9999),
      defaultSortOrder: 'ascend',
      render: (position: number | null) => position !== null ? (
        <Tag color="purple" style={{ minWidth: 24, textAlign: 'center' }}>{position}</Tag>
      ) : (
        <Typography.Text type="secondary">-</Typography.Text>
      ),
    },
    {
      title: 'API Name',
      dataIndex: 'api_name',
      key: 'api_name',
      sorter: (a, b) => a.api_name.localeCompare(b.api_name),
      render: (apiName: string, record: ForecastField) => (
        <Space>
          <Typography.Text code style={{ fontSize: 12 }}>{apiName}</Typography.Text>
          {record.deleted && <Tag color="red">Deleted</Tag>}
        </Space>
      ),
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      render: (name: string) => (
        <Typography.Text style={{ fontSize: 12 }}>{name}</Typography.Text>
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      render: (unit: string | null) => (
        <Typography.Text style={{ fontSize: 12 }}>{unit || '-'}</Typography.Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'forecast_types',
      key: 'forecast_types',
      width: 180,
      sorter: (a, b) => a.forecast_types.join(',').localeCompare(b.forecast_types.join(',')),
      render: (_, record) => (
        <Space size={4} wrap>
          {record.forecast_types.map(type => (
            <Tag key={type} color={FORECAST_TYPE_COLORS[type]} style={{ margin: 0 }}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Tag>
          ))}
        </Space>
      ),
      filters: [
        { text: 'Daily', value: 'daily' },
        { text: 'Hourly', value: 'hourly' },
        { text: 'Both', value: 'both' },
        { text: 'Minutely', value: 'minutely' },
        { text: 'All', value: 'all' },
      ],
      onFilter: (value, record) => {
        const forecastTypes = record.forecast_types;
        if (value === 'both') return forecastTypes.length === 2 && forecastTypes.includes('daily') && forecastTypes.includes('hourly');
        if (value === 'all') return FORECAST_TYPES.every(type => forecastTypes.includes(type));
        return forecastTypes.includes(value as ForecastType);
      },
    },
    {
      title: 'Calculated',
      dataIndex: 'calculated',
      key: 'calculated',
      width: 130,
      sorter: (a, b) => Number(a.calculated) - Number(b.calculated),
      render: (calculated: boolean) => (
        <Tooltip title={calculated ? 'Computed locally from other data' : 'Fetched from Tomorrow.io API'}>
          <Tag color={calculated ? 'cyan' : 'default'}>
            {calculated ? 'Yes' : 'No'}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.calculated === value,
    },
    {
      title: 'Hidden',
      dataIndex: 'hidden',
      key: 'hidden',
      width: 110,
      sorter: (a, b) => Number(a.hidden) - Number(b.hidden),
      render: (hidden: boolean) => (
        <Tooltip title={hidden ? 'Used only for calculations, not shown in forecast view' : 'Displayed in forecast view'}>
          <Tag color={hidden ? 'red' : 'default'}>
            {hidden ? 'Yes' : 'No'}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.hidden === value,
    },
    {
      title: 'Demo',
      dataIndex: 'demo_only',
      key: 'demo_only',
      width: 100,
      sorter: (a, b) => Number(a.demo_only) - Number(b.demo_only),
      render: (demoOnly: boolean) => (
        <Tooltip title={demoOnly ? 'Only available for demo events (for testing new fields)' : 'Available for all events'}>
          <Tag color={demoOnly ? 'orange' : 'default'}>
            {demoOnly ? 'Yes' : 'No'}
          </Tag>
        </Tooltip>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.demo_only === value,
    },
    {
      title: 'View Visibility',
      key: 'view_visibility',
      width: 240,
      render: (_, record) => {
        const items = [
          { key: 'hc', label: 'H Cards', value: record.show_in_hourly_cards },
          { key: 'ht', label: 'H Table', value: record.show_in_hourly_table },
          { key: 'dc', label: 'D Cards', value: record.show_in_daily_cards },
          { key: 'dt', label: 'D Table', value: record.show_in_daily_table },
          { key: 'mc', label: 'Map Card', value: record.show_in_map_card },
        ];
        return (
          <Space size={4} wrap>
            {items.map(item => (
              <Tooltip key={item.key} title={`${item.value ? 'Shown' : 'Hidden'} in ${item.label.replace('H ', 'Hourly ').replace('D ', 'Daily ')}`}>
                <Tag 
                  color={item.value ? 'green' : 'default'}
                  style={{ margin: 0, fontSize: 11, padding: '0 4px' }}
                >
                  {item.label}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Card Group',
      key: 'card_group',
      width: 240,
      sorter: (a, b) => (a.group_position ?? 9999) - (b.group_position ?? 9999),
      render: (_, record) => record.card_group ? (
        <Space size={4}>
          {record.group_position !== null && (
            <Tooltip title="Group position">
              <Tag color="orange" style={{ minWidth: 24, textAlign: 'center' }}>{record.group_position}</Tag>
            </Tooltip>
          )}
          <Tag color="geekblue">{record.card_group}</Tag>
          {record.group_display_name && (
            <Tooltip title="Group label">
              <Tag color="cyan">{record.group_display_name}</Tag>
            </Tooltip>
          )}
        </Space>
      ) : (
        <Typography.Text type="secondary">-</Typography.Text>
      ),
    },
    {
      title: 'Updated',
      key: 'update_info',
      width: 180,
      render: (_, record) => (
        <Space orientation="vertical" size={0} style={{ fontSize: 11 }}>
          {record.updated_by && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>{record.updated_by}</Typography.Text>
          )}
          <Typography.Text type="secondary" style={{ fontSize: 10 }}>
            {formatUpdateDatetime(record.update_datetime)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          ...(record.deleted
            ? [
                {
                  key: 'restore',
                  icon: <RollbackOutlined />,
                  label: 'Restore',
                  onClick: () => handleRestore(record.api_name),
                },
              ]
            : [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Edit',
                  onClick: () => handleOpenModal(record),
                },
                { type: 'divider' as const },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Soft Delete',
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Soft Delete Forecast Field',
                      content: (
                        <div>
                          Are you sure you want to soft-delete <Typography.Text code>{record.api_name}</Typography.Text>?
                          <br />
                          <Typography.Text type="secondary">The field can be restored later from the list.</Typography.Text>
                        </div>
                      ),
                      okText: 'Yes, Soft Delete',
                      cancelText: 'Cancel',
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record.api_name),
                    });
                  },
                },
              ]),
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Title level={2} style={{ margin: 0 }}>Forecast Fields Management</Title>
          <Space>
            <Button
              icon={<GroupOutlined />}
              onClick={handleOpenGroupReorderModal}
              disabled={!hasGroups}
            >
              Reorder in Group
            </Button>
            <Button
              icon={<OrderedListOutlined />}
              onClick={handleOpenReorderModal}
              disabled={fields.filter(f => !f.deleted).length < 2}
            >
              Reorder Fields
            </Button>
            <Checkbox
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            >
              Include deleted
            </Checkbox>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              Add Forecast Field
            </Button>
          </Space>
        </div>

        <Alert
          title="Forecast Fields Configuration"
          description={
            <span>
              Configure which weather parameters are fetched from Tomorrow.io and displayed in forecasts.
              <strong> Calculated</strong> fields are computed locally from other data.
              <strong> Hidden</strong> fields are used for calculations but not shown in the UI.
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
        />

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={fields}
            rowKey="api_name"
            loading={loading}
            pagination={false}
            scroll={tableScrollY != null ? { y: tableScrollY } : undefined}
            size="small"
            footer={() => (
              <div style={{ textAlign: 'right', color: '#8c8c8c', fontSize: 12 }}>
                {fields.length} field{fields.length !== 1 ? 's' : ''}
              </div>
            )}
          />
        </div>
      </Card>

      {/* Modal */}

      <Modal
        title={editingField ? 'Edit Forecast Field' : 'Add Forecast Field'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        width={560}
        okText={editingField ? 'Update' : 'Create'}
        confirmLoading={saving}
        okButtonProps={{ disabled: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          {/* Row 1: API Name & DB Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="api_name"
              label="API Name"
              rules={[
                { required: true, message: 'Required' },
                { pattern: /^[a-zA-Z0-9_]+$/, message: 'Invalid format' },
              ]}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., temperatureMax" disabled={!!editingField} />
            </Form.Item>

            <Form.Item
              name="db_name"
              label="DB Name"
              rules={[{ required: true, message: 'Required' }]}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., temp" />
            </Form.Item>
          </div>

          {/* Row 2: Display Name & Unit */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="display_name"
              label="Display Name"
              rules={[{ required: true, message: 'Required' }]}
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., Temperature Min" />
            </Form.Item>

            <Form.Item
              name="unit"
              label="Unit"
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., °C, %" />
            </Form.Item>
          </div>

          {/* Row 3: Card Group, Group Display Name & Position */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 16 }}>
            <Form.Item
              name="card_group"
              label="Card Group"
              tooltip="Group name to combine fields in cards view (e.g., 'Temperature', 'Wind')"
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., Temperature" />
            </Form.Item>

            <Form.Item
              name="group_display_name"
              label="Group Label"
              tooltip="Short label when shown within a group (e.g., 'Min', 'Max')"
              style={{ marginBottom: 12 }}
            >
              <Input placeholder="e.g., Min" />
            </Form.Item>

            <Form.Item
              name="group_position"
              label="Grp Pos"
              tooltip="Order within group (lower = first)"
              style={{ marginBottom: 12 }}
            >
              <InputNumber min={1} placeholder="1" style={{ width: '100%' }} />
            </Form.Item>
          </div>

          {/* Row 4: Position, Forecast Type & Switches */}
          <div style={{ display: 'grid', gridTemplateColumns: '80px 2fr 1fr 1fr', gap: 16, alignItems: 'end' }}>
            <Form.Item
              name="position"
              label="Position"
              tooltip="Overall field order (lower = first)"
              style={{ marginBottom: 12 }}
            >
              <InputNumber min={1} placeholder="1" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="forecast_types"
              label="Forecast Types"
              rules={[{ required: true, message: 'Required' }]}
              initialValue={['daily', 'hourly']}
              style={{ marginBottom: 12 }}
            >
              <Select
                mode="multiple"
                maxTagCount="responsive"
                options={FORECAST_TYPE_OPTIONS}
                placeholder="Select forecast types"
                popupRender={menu => (
                  <>
                    {menu}
                    <Space size={8} style={{ padding: '8px 12px' }}>
                      <Button size="small" onClick={() => form.setFieldValue('forecast_types', [...FORECAST_TYPES])}>
                        All
                      </Button>
                    </Space>
                  </>
                )}
              />
            </Form.Item>

            <Form.Item
              name="calculated"
              label="Calculated"
              valuePropName="checked"
              initialValue={false}
              style={{ marginBottom: 12 }}
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="hidden"
              label="Hidden"
              valuePropName="checked"
              initialValue={false}
              style={{ marginBottom: 12 }}
            >
              <Switch />
            </Form.Item>
          </div>

          {/* Row 5: Demo Only */}
          <div style={{ marginBottom: 12 }}>
            <Form.Item
              name="demo_only"
              label="Demo Only"
              valuePropName="checked"
              initialValue={false}
              tooltip="If enabled, this field will only be available for demo events (allows testing new fields before production rollout)"
              style={{ marginBottom: 0 }}
            >
              <Switch />
            </Form.Item>
          </div>

          {/* View Visibility Section */}
          <div style={{ 
            border: '1px solid #d9d9d9', 
            borderRadius: 8, 
            padding: 12, 
            background: '#fafafa'
          }}>
            <Typography.Text strong style={{ fontSize: 13 }}>
              View Visibility
            </Typography.Text>
            <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>
              (where this field appears)
            </Typography.Text>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginTop: 10 }}>
              <Form.Item
                name="show_in_hourly_cards"
                label="Hourly Cards"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="show_in_hourly_table"
                label="Hourly Table"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="show_in_daily_cards"
                label="Daily Cards"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="show_in_daily_table"
                label="Daily Table"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="show_in_map_card"
                label="Map Card"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Reorder Modal */}
      <Modal
        title="Reorder Forecast Fields"
        open={isReorderModalOpen}
        onOk={handleSaveOrder}
        onCancel={handleCloseReorderModal}
        width={600}
        okText="Save Order"
        confirmLoading={savingOrder}
        okButtonProps={{ disabled: savingOrder }}
        cancelButtonProps={{ disabled: savingOrder }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">Drag fields to reorder them. Click "Save Order" to apply changes.</Typography.Text>
        </div>
        <div style={{ 
          maxHeight: 400, 
          overflowY: 'auto',
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          padding: 8,
        }}>
          {reorderFields.map((field, index) => (
            <div
              key={field.api_name}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                marginBottom: index < reorderFields.length - 1 ? 4 : 0,
                background: dragOverIndex === index ? '#e6f4ff' : '#fafafa',
                borderRadius: 6,
                cursor: 'grab',
                border: dragOverIndex === index ? '2px dashed #1890ff' : '1px solid #f0f0f0',
                transition: 'all 0.2s ease',
                userSelect: 'none',
              }}
            >
              <HolderOutlined style={{ color: '#999', fontSize: 16 }} />
              <Tag color="purple" style={{ minWidth: 28, textAlign: 'center' }}>
                {index + 1}
              </Tag>
              <div style={{ flex: 1 }}>
                <Typography.Text strong>{field.display_name}</Typography.Text>
                <Typography.Text type="secondary" style={{ marginLeft: 8 }}>({field.api_name})</Typography.Text>
              </div>
              <Space size={4} wrap>
                {field.forecast_types.map(type => (
                  <Tag key={type} color={FORECAST_TYPE_COLORS[type]} style={{ margin: 0 }}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Tag>
                ))}
              </Space>
              {field.unit && (
                <Typography.Text type="secondary">{field.unit}</Typography.Text>
              )}
            </div>
          ))}
        </div>
      </Modal>

      {/* Group Reorder Modal - Reorder fields within a group */}
      <Modal
        title="Reorder Fields in Group"
        open={isGroupReorderModalOpen}
        onOk={handleSaveGroupFieldOrder}
        onCancel={handleCloseGroupReorderModal}
        width={600}
        okText="Save Order"
        confirmLoading={savingGroupOrder}
        okButtonProps={{ disabled: savingGroupOrder || !selectedGroup }}
        cancelButtonProps={{ disabled: savingGroupOrder }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            Select a group and drag fields to reorder them within that group.
          </Typography.Text>
        </div>

        {/* Group Selector */}
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Select Group:
          </Typography.Text>
          <Select
            placeholder="Choose a group to reorder..."
            value={selectedGroup}
            onChange={handleSelectGroup}
            style={{ width: '100%' }}
            options={availableGroups.map(g => ({
              value: g.name,
              label: (
                <span>
                  {g.name} <Typography.Text type="secondary">({g.fieldCount} fields)</Typography.Text>
                </span>
              ),
            }))}
          />
        </div>

        {/* Fields in selected group */}
        {selectedGroup && groupFieldsToReorder.length > 0 && (
          <div style={{ 
            maxHeight: 350, 
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: 8,
          }}>
            {groupFieldsToReorder.map((field, index) => (
              <div
                key={field.api_name}
                draggable
                onDragStart={(e) => handleGroupFieldDragStart(e, index)}
                onDragEnd={handleGroupFieldDragEnd}
                onDragOver={(e) => handleGroupFieldDragOver(e, index)}
                onDragLeave={handleGroupFieldDragLeave}
                onDrop={(e) => handleGroupFieldDrop(e, index)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  marginBottom: index < groupFieldsToReorder.length - 1 ? 4 : 0,
                  background: dragOverGroupFieldIndex === index ? '#e6f4ff' : '#fafafa',
                  borderRadius: 6,
                  cursor: 'grab',
                  border: dragOverGroupFieldIndex === index ? '2px dashed #1890ff' : '1px solid #f0f0f0',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                }}
              >
                <HolderOutlined style={{ color: '#999', fontSize: 16 }} />
                <Tag color="orange" style={{ minWidth: 28, textAlign: 'center' }}>
                  {index + 1}
                </Tag>
                <div style={{ flex: 1 }}>
                  <Typography.Text strong>{field.group_display_name || field.display_name}</Typography.Text>
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>({field.api_name})</Typography.Text>
                </div>
                {field.unit && (
                  <Typography.Text type="secondary">{field.unit}</Typography.Text>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty state when no group is selected */}
        {!selectedGroup && (
          <div style={{ 
            padding: 24, 
            textAlign: 'center', 
            background: '#fafafa', 
            borderRadius: 8,
            border: '1px dashed #d9d9d9',
          }}>
            <Typography.Text type="secondary">
              Select a group above to reorder its fields
            </Typography.Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ForecastFieldsManagement;

